import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import jwtDecode from "jwt-decode";
import { Alert, Button, CircularProgress, CssBaseline, Box, Snackbar, Typography } from "@mui/material";
import { useLocation, useParams } from "react-router";
import Details from "../components/campaigns/Details";
import DashboardHeaderSection from "../components/common/dashboardheader/DashboardHeader";
import Swal from "sweetalert2";
import paymentportalservice from "../services/paymentportalservice";
import portalpaymentlink from "../services/portalpaymentlink";
import CreateQueueModal from "../components/paymentqueues/CreateQueueModal";
import AddRevenueDialog from "../components/campaigns/common/AddRevenueDialog";
import EditBudgetLimitDialog from "../components/campaigns/common/EditBudgetLimitDialog";
import ShareCampaignDialog from "../components/campaigns/common/ShareCampaignDialog";
import { normalizeCampaignDetails } from "../components/campaigns/common/campaignFieldUtils";
import GetCookieByName from "../hooks/GetCookie";

function getErrorMessage(error, fallback) {
  const data = error?.response?.data;
  return (typeof data === "string" ? data : data?.message) || error?.message || fallback;
}

// The URL param is the source of truth for which campaign is shown; the
// router `state` (passed by CampaignCard) is only an optimistic first paint.
// Old history entries without an :id still work via state.id. Keying the page
// on the campaign id remounts it when navigating between campaigns, so no
// state (details, tabs, dialogs) leaks from the previous one.
export default function CampaignDetails() {
  const { id } = useParams();
  const location = useLocation();
  const campaignId = id || location.state?.id;

  return <CampaignDetailsPage key={campaignId} campaignId={campaignId} initialDetails={location.state} />;
}

function CampaignDetailsPage({ campaignId, initialDetails }) {
  useEffect(() => { document.title = "Campaign Details | Meesha"; }, []);

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  const [campaignDetails, setCampaignDetails] = useState(initialDetails || null);
  const [campaignInfo, setCampaignInfo] = useState([]);
  const [paymentQueue, setPaymentQueue] = useState([]);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  // Starts true so the first paint doesn't flash content → spinner → content.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Same super-user gate and dialog state as CampaignsDashboard, so the
  // card here shows the same action icons as on the campaigns list.
  const isSuperUser = useMemo(() => {
    try {
      const token = JSON.parse(GetCookieByName("auth"))["token"];
      return Boolean(jwtDecode(token)?.isSuperUser);
    } catch (error) {
      return false;
    }
  }, []);

  const [revenueDialogCampaign, setRevenueDialogCampaign] = useState(null);
  const [budgetLimitDialogCampaign, setBudgetLimitDialogCampaign] = useState(null);
  const [shareDialogCampaign, setShareDialogCampaign] = useState(null);

  // Update endpoints return the raw campaign document; normalizing against
  // the current value keeps the page's flattened shape and any field the
  // response doesn't populate.
  const patchCampaign = useCallback((updated) => {
    if (!updated) return;
    setCampaignDetails((prev) => normalizeCampaignDetails(updated, prev || {}));
  }, []);

  // Bumped on every fetch and on unmount, so a response that resolves after
  // a newer request (or after leaving the page) is dropped instead of
  // overwriting fresher state.
  const requestIdRef = useRef(0);

  const fetchCampaign = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    if (!campaignId) {
      setError("No campaign selected.");
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const postdata = { id: campaignId };

      // allSettled so a failing status/queue endpoint doesn't also throw
      // away the campaign details (and vice versa).
      const [detailsResult, linksResult, queueResult] = await Promise.allSettled([
        portalpaymentlink.getCampaignDetails(postdata),
        paymentportalservice.FetchCampaignDetailStatus(postdata),
        portalpaymentlink.fetchPaymentQueueLinks(campaignId)
      ]);

      if (requestId !== requestIdRef.current) return;

      if (linksResult.status === "fulfilled") {
        setCampaignInfo(linksResult.value || []);
      } else {
        console.error(`Campaign status fetch failed: `, linksResult.reason);
      }

      if (queueResult.status === "fulfilled") {
        setPaymentQueue(queueResult.value?.success ? queueResult.value.processed : []);
      } else {
        console.error(`Payment queue fetch failed: `, queueResult.reason);
      }

      const detailsResp = detailsResult.status === "fulfilled" ? detailsResult.value : null;
      if (!detailsResp) {
        console.error(`Campaign details fetch failed: `, detailsResult.reason);
        setError(getErrorMessage(detailsResult.reason, "Could not load campaign details."));
        return;
      }

      setCampaignDetails((prev) => normalizeCampaignDetails(detailsResp, { ...prev, id: campaignId }));
    }
    catch (error) {
      if (requestId !== requestIdRef.current) return;
      console.error(`In campaign details initialize error: `, error);
      setError(getErrorMessage(error, "Could not load campaign details."));
    }
    finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    fetchCampaign();
    // Invalidates any in-flight request on unmount. requestIdRef is a plain
    // counter, not a DOM ref, so reading its latest value here is intended.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => { requestIdRef.current++; };
  }, [fetchCampaign]);

  const handleFreezeToggle = useCallback(async (campaign) => {
    const freezing = !campaign.isFrozen;

    const confirm = await Swal.fire({
      icon: "warning",
      title: freezing ? "Freeze this campaign?" : "Unfreeze this campaign?",
      text: freezing
        ? "No one will be able to edit it until a super user unfreezes it."
        : "Editing will be allowed again for everyone.",
      showCancelButton: true,
      confirmButtonText: freezing ? "Freeze" : "Unfreeze",
    });
    if (!confirm.isConfirmed) return;

    try {
      const updated = await paymentportalservice.SetCampaignFrozen({
        campaignId: campaign._id || campaign.id,
        freeze: freezing
      });
      patchCampaign({ isFrozen: freezing, ...updated });
      Swal.fire({
        icon: "success",
        title: freezing ? "Campaign frozen" : "Campaign unfrozen",
        showConfirmButton: false,
        timer: 1200,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Could not update freeze status",
        text: getErrorMessage(error),
      });
    }
  }, [patchCampaign]);

  const handleCreateLink = useCallback(() => {
    if (Number(campaignDetails?.paymentLimit) <= 0) {
      Swal.fire({
        position: "center",
        icon: "error",
        title: "Payment Limit Exceeded",
        showConfirmButton: false,
        timer: 2000,
      });
      return;
    }

    setOpenCreateModal(true);
  }, [campaignDetails?.paymentLimit]);

  const handleQueueCreated = useCallback((processed, skipped) => {
    Swal.fire({
      title: 'Payment Queue Process',
      text: `${processed.length} Links Processed, ${skipped.length} Links Skipped`,
      icon: 'success'
    });

    setOpenCreateModal(false);
    fetchCampaign();
  }, [fetchCampaign]);

  const closeCreateModal = useCallback(() => setOpenCreateModal(false), []);
  const closeRevenueDialog = useCallback(() => setRevenueDialogCampaign(null), []);
  const closeBudgetLimitDialog = useCallback(() => setBudgetLimitDialogCampaign(null), []);
  const closeShareDialog = useCallback(() => setShareDialogCampaign(null), []);
  const clearError = useCallback(() => setError(null), []);

  let content = null;

  // Full-page spinner only while there's nothing to show yet — refetches
  // (e.g. after creating a queue) keep Details mounted so the selected tab,
  // scroll position and open dialogs survive.
  if (loading && !campaignDetails) {
    content = (
      <Box
        sx={{
          width: "100%",
          height: 600,
          mt: 12,
          display: 'flex',
          justifyContent: 'center'
        }}
      >
        <CircularProgress size={24} color="primary" sx={{ mr: 1 }} />
        <Typography fontSize={16} fontFamily={'Quicksand'} fontWeight={600}>
          Loading Campaign Details...
        </Typography>
      </Box>
    );
  } else if (campaignDetails) {
    content = (
      <>
        <Details
          campaignDetails={campaignDetails}
          handleLinkGenerate={handleCreateLink}
          campaignInfo={campaignInfo}
          isDisabled={true}
          paymentQueue={paymentQueue}
          isSuperUser={isSuperUser}
          onAddRevenue={setRevenueDialogCampaign}
          onEditBudgetLimit={setBudgetLimitDialogCampaign}
          onFreezeToggle={handleFreezeToggle}
          onShare={setShareDialogCampaign}
        />

        <CreateQueueModal
          open={openCreateModal}
          onClose={closeCreateModal}
          campaignId={campaignId}
          onSuccess={handleQueueCreated}
        />

        <AddRevenueDialog
          open={Boolean(revenueDialogCampaign)}
          campaign={revenueDialogCampaign}
          onClose={closeRevenueDialog}
          onSaved={patchCampaign}
        />

        <EditBudgetLimitDialog
          open={Boolean(budgetLimitDialogCampaign)}
          campaign={budgetLimitDialogCampaign}
          onClose={closeBudgetLimitDialog}
          onSaved={patchCampaign}
        />

        <ShareCampaignDialog
          key={shareDialogCampaign?.id || "none"}
          open={Boolean(shareDialogCampaign)}
          campaign={shareDialogCampaign}
          onClose={closeShareDialog}
        />

        <Snackbar
          open={Boolean(error)}
          autoHideDuration={6000}
          onClose={clearError}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity="error" onClose={clearError} sx={{ width: "100%" }}>
            {error}
          </Alert>
        </Snackbar>
      </>
    );
  } else if (error) {
    content = (
      <Box sx={{ mt: 12, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
        <Typography fontSize={16} fontFamily={'Quicksand'} fontWeight={600}>
          {error}
        </Typography>
        <Button variant="contained" onClick={fetchCampaign}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <>
      <CssBaseline />
      <DashboardHeaderSection hideSearch />
      {content}
    </>
  );
}
